import { createTheme, Grid, Hidden, makeStyles, MuiThemeProvider } from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'formik';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  BILLING_CLAIMS_SELECTOR_OPTIONS,
  BILLING_CLAIMS_STATUSES,
  CLAIM_STATUS_SELECTOR_OPTIONS,
  PAYMENT_METHOD_CLAIMS_SELECTOR_OPTIONS,
} from '../../constants';
import CptInfo from './../widgets/CptInfo';
import Select from '../../../FormikFields/ComplexSelect/Select';
import DataInput from '../../../FormikFields/DataInput';
import FormikTextField from '../../../FormikFields/FormikTextField';
import defaultTheme from '../../../../config/theme';
import { addDays, getFormattedDate } from '../../../../helpers/localize';
import BlurScreen from '../../../Shared/BlurScreen';
import { useSelector } from 'react-redux';

const customTheme = createTheme({
  ...defaultTheme,
  breakpoints: {
    values: {
      xs: 0,
      sm: 800,
      md: 1000,
      lg: 1200,
      xl: 1400,
    },
  },
});

const useStyles = makeStyles((theme) => ({
  mb1: {
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(1.5),
    },
  },
  form: {
    position: 'relative',
  },
  datepickerWrapperClassName: {
    padding: '3px 12px',
  },
  loaderContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  selector: {
    backgroundColor: '#D9D9D9',
  },
  acceptResultBtnLabel: {
    color: theme.palette.common.white,
    letterSpacing: 2,
    fontWeight: 700,
  },
  acceptResult: {
    background: theme.colors.green,
    padding: theme.spacing(1.25, 2),
  },
  tableInput: {
    marginTop: 0,
  },
}));

const ClaimForm = ({
                     claim,
                     setClaim,
                     errors,
                     backendErrors,
                     setBackendErrors,
                     cptCodes,
                     modifiers,
                     insuranceProviders,
                     appointmentProviders,
                     isAllDataNotLoaded,
                     isDistributionSponsor,
                     initActivities,
                     navWidth,
                   }) => {
  const classes = useStyles();
  const { en: { forms } } = useSelector((state) => state.i18n);
  const [filteredInsuranceProviders, setInsuranceProviders] = useState(insuranceProviders);
  const [prevManuallySetClaimStatus, setPrevManuallySetClaimStatus] = useState(null);
  const [prevPaymentDate, setPrevPaymentDate] = useState([]);

  useEffect(() => {
    setInsuranceProviders(insuranceProviders);
  }, [insuranceProviders]);

  useEffect(() => {
    if (prevManuallySetClaimStatus == null) {
      setPrevManuallySetClaimStatus(claim.claimStatus);
    }
  }, [claim]);

  const updateBackendErrorsIfNeeded = (ids) => {
    ids.forEach((id) => {
      if (backendErrors[id]) {
        const newBackendErrors = { ...backendErrors };
        delete newBackendErrors[id];
        setBackendErrors(newBackendErrors);
      }
    });
  };

  const updateCPTErrorsIfNeeded = (id, key) => {
    if (backendErrors[id] || backendErrors.activities) {
      if (backendErrors[id] && typeof backendErrors[id] === 'string') {
        updateBackendErrorsIfNeeded([id]);
      } else {
        if (backendErrors[id]) {
          const newBackendError = { ...backendErrors };
          const newError = { ...newBackendError[id] };
          newError[id] && newError[id][key] && delete newError[id][key];
          newBackendError.activities && delete newBackendError.activities;
          setBackendErrors({
            ...newBackendError,
            [id]: newError,
          });
        } else {
          const newBackendError = { ...backendErrors };
          newBackendError.activities && delete newBackendError.activities;
          setBackendErrors({
            ...newBackendError,
          });
        }
      }
    }
  };

  const onDismiss = (id) => {
    updateBackendErrorsIfNeeded([id]);
    const newCpt = claim && claim.activities.filter((c) => {
      return id !== c.id;
    });
    updateClaimValue('activities', newCpt);
  };

  const getAutoUpdatedValues = (key, value) => {
    let autoUpdatedValues = {};
    switch (key) {
      case 'isPatientSeen': {
        if (value === false) {
          autoUpdatedValues = { billingStatus: 'appointmentCancelled' };
        }
        break;
      }
      case 'documentDate': {
        if (value) {
          autoUpdatedValues = { isDocumentUploaded: true };
        }
        break;
      }
      case 'billingStatus': {
        if (value === BILLING_CLAIMS_STATUSES.insuranceIssue
          || value === BILLING_CLAIMS_STATUSES.appointmentTypeIssue) {
          autoUpdatedValues = { claimStatus: 'notBillable' };
        } else if (value === BILLING_CLAIMS_STATUSES.appointmentCancelled) {
          autoUpdatedValues = {
            isPatientSeen: false,
            claimStatus: 'notBillable',
          };
        } else if (value === BILLING_CLAIMS_STATUSES.billed) {
          autoUpdatedValues = {
            isPatientSeen: true,
            claimStatus: 'billed',
          };
        } else if (value === BILLING_CLAIMS_STATUSES.pending
          || value === BILLING_CLAIMS_STATUSES.hold) {
          autoUpdatedValues = {
            claimStatus: 'pending',
          };
        } else {
          autoUpdatedValues = { claimStatus: prevManuallySetClaimStatus };
        }
        break;
      }
      case 'claimStatus': {
        setPrevManuallySetClaimStatus(value);
        break;
      }
      default: {
        break;
      }
    }

    return autoUpdatedValues;
  };

  const autoUpdateClaimStatusIfNeeded = (originalClaim, checkPaymentDate) => {
    let claim = JSON.parse(JSON.stringify(originalClaim));
    if (claim.activities) {
      let isAllPaid = claim.activities.length > 0;
      let isAllPatientResponsibility = claim.activities.length > 0;
      let isAllDenied = claim.activities.length > 0;
      let isPartiallyPaid = false;
      let paymentDate;

      claim.activities.forEach(e => {
        if (e.cptStatus !== 'paid') {
          isAllPaid = false;
        }
        if (e.cptStatus !== 'denied') {
          isAllDenied = false;
        }
        if (e.cptStatus !== 'patientResponsibility') {
          isAllPatientResponsibility = false;
        }
      });

      let hasNotPaidCPT = false;
      const paidSumm = claim.activities.map((activity) => {
          const paid = activity.paid;
          if (!parseFloat(paid || 0)) {
            hasNotPaidCPT = true;
          }
          return isNaN(parseFloat(paid ?? 0)) ? 0 : parseFloat(paid ?? 0);
        },
      ).reduce((a, b) => a + b, 0);

      if (hasNotPaidCPT && paidSumm > 0) {
        isPartiallyPaid = true;
      }

      const allPRisNotNull = claim.activities.map((activity) => {
          const PR = activity.pr;
          return isNaN(parseFloat(PR ?? 0)) ? 0 : parseFloat(PR ?? 0);
        },
      ).reduce((a, b) => a * b, 1) !== 0 && claim.activities.length;

      if (paidSumm > 0 || (allPRisNotNull && paidSumm === 0)) {
        const date = new Date();
        const lastDay = new Date(date.getFullYear(), date.getMonth(), 0);
        if (!isAllPaid && paidSumm > 0) {
          isAllPaid = true;
        } else if (!isAllPatientResponsibility && allPRisNotNull && paidSumm === 0) {
          isAllPatientResponsibility = true;
        }

        paymentDate = getFormattedDate(lastDay);
      }

      if (claim.billingStatus === BILLING_CLAIMS_STATUSES.pending
        || claim.billingStatus === BILLING_CLAIMS_STATUSES.hold) {
        claim = {
          ...claim,
          claimStatus: 'pending',
        };
      } else if (claim.billingStatus === BILLING_CLAIMS_STATUSES.insuranceIssue
        || claim.billingStatus === BILLING_CLAIMS_STATUSES.appointmentCancelled
        || claim.billingStatus === BILLING_CLAIMS_STATUSES.appointmentTypeIssue) {
        claim = {
          ...claim,
          claimStatus: 'notBillable',
        };
      } else if (isAllPaid || isAllPatientResponsibility || isAllDenied || isPartiallyPaid) {
        claim = {
          ...claim,
          claimStatus: isPartiallyPaid
            ? 'partiallyPaid'
            : isAllPaid
              ? 'paid'
              : isAllDenied
                ? 'denied'
                : isAllPatientResponsibility
                  ? 'patientResponsibility'
                  : claim.billingStatus === BILLING_CLAIMS_STATUSES.billed
                    ? 'billed'
                    : prevManuallySetClaimStatus,
        };
      } else {
        claim = {
          ...claim,
          claimStatus: claim.billingStatus === BILLING_CLAIMS_STATUSES.billed
            ? 'billed'
            : prevManuallySetClaimStatus,
        };
      }

      claim = {
        ...claim,
        activities: claim.activities.map((item, index) => {
          return {
            ...item,
            paymentDate: claim.billingStatus === BILLING_CLAIMS_STATUSES.insuranceIssue
            || claim.billingStatus === BILLING_CLAIMS_STATUSES.appointmentCancelled
            || claim.billingStatus === BILLING_CLAIMS_STATUSES.appointmentTypeIssue
              ? ''
              : !checkPaymentDate
                ? item.paymentDate
                : item.paymentDate || prevPaymentDate.find(i => i.id === item.id)?.paymentDate || paymentDate || '',
          };
        }),
      };
    }
    return claim;
  };

  const updateClaimValue = (key, value) => {
    if (key === 'activities') {
      setPrevPaymentDate(claim.activities);
    }

    const autoUpdatedValues = getAutoUpdatedValues(key, value);

    updateBackendErrorsIfNeeded([
      ...Object.keys(autoUpdatedValues),
      key,
    ]);

    let newClaim = {
      ...claim,
      ...autoUpdatedValues,
      [key]: value,
    };

    if (key === 'activities' || key === 'billingStatus') {
      newClaim = autoUpdateClaimStatusIfNeeded(newClaim, key !== 'activities');
    }

    setClaim(newClaim);
  };

  const claimStatusDisabledItems = useMemo(
    () => {
      if (claim) {
        if (claim.billingStatus
          && (claim.billingStatus === BILLING_CLAIMS_STATUSES.pending
            || claim.billingStatus === BILLING_CLAIMS_STATUSES.hold)) {
          return CLAIM_STATUS_SELECTOR_OPTIONS.filter(item => item.value !== 'pending').map(item => item.value);
        }
        if (claim
          && claim.billingStatus
          && (claim.billingStatus === BILLING_CLAIMS_STATUSES.appointmentCancelled
            || claim.billingStatus === BILLING_CLAIMS_STATUSES.insuranceIssue
            || claim.billingStatus === BILLING_CLAIMS_STATUSES.appointmentTypeIssue
          )) {
          return CLAIM_STATUS_SELECTOR_OPTIONS.filter(item => item.value !== 'notBillable').map(item => item.value);
        }

        let disabledItems = CLAIM_STATUS_SELECTOR_OPTIONS.filter(item =>
          item.value === 'paid'
          || item.value === 'partiallyPaid'
          || item.value === 'patientResponsibility'
          || item.value === 'denied',
        ).map(item => item.value);

        if (claim?.activities) {
          let isAllPaid = claim?.activities.every(item => +item.paid > 0);
          if (isAllPaid) {
            disabledItems = disabledItems.filter(item => item !== 'paid');
          }
          let isPartiallyPaid = claim?.activities.some(item => +item.paid > 0);
          if (isPartiallyPaid) {
            disabledItems = disabledItems.filter(item => item !== 'partiallyPaid');
          }
          let isAllPatientResponsibility = claim?.activities.every(item => +item.pr > 0);
          if (isAllPatientResponsibility) {
            disabledItems = disabledItems.filter(item => item !== 'patientResponsibility');
          }
          let isAllDenied = claim?.activities.every(item => !+item.pr && !+item.paid);
          if (isAllDenied) {
            disabledItems = disabledItems.filter(item => item !== 'denied');
          }
        }
        return disabledItems;
      }

      return CLAIM_STATUS_SELECTOR_OPTIONS.filter(item =>
        item.value === 'paid'
        || item.value === 'partiallyPaid'
        || item.value === 'patientResponsibility'
        || item.value === 'denied',
      ).map(item => item.value);
    },
    [claim?.billingStatus, claim?.activities],
  );

  return (
    <BlurScreen isBlurShown={isAllDataNotLoaded}>
      <MuiThemeProvider theme={customTheme}>
        <Form className={classes.form}>
          <Grid
            container
            justify='flex-start'
            spacing={3}
            alignItems='flex-start'
            className={classes.mb1}
          >
            <>
              <Grid item xl={9} lg={8} md={6} sm={6} xs={0} />
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <Select
                  required={true}
                  disabled={!isDistributionSponsor}
                  dataKey='billingStatus'
                  filterValue={claim && claim.billingStatus}
                  columnData={{
                    isFilterable: true,
                    options: BILLING_CLAIMS_SELECTOR_OPTIONS,
                  }}
                  onChange={updateClaimValue}
                  label={forms.billingStatus}
                  labelWidth={78}
                  error={errors.billingStatus || backendErrors.billingStatus}
                  className={cx(classes.margin0, classes.fullWidth)}
                />
              </Grid>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <FormikTextField
                  disabled={!isDistributionSponsor}
                  required={true}
                  fullWidth
                  type='text'
                  hideErrorAres={true}
                  name='location'
                  label={forms.location}
                  backendError={backendErrors.location}
                  onChange={(e) =>
                    updateClaimValue('location', e.currentTarget.value)
                  }
                  className={classes.tableInput}
                />
              </Grid>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <Select
                  filterValue={claim.appointmentProvider}
                  disabled={!isDistributionSponsor || !appointmentProviders}
                  required={false}
                  dataKey='appointmentProvider'
                  columnData={{
                    isFilterable: false,
                    options: appointmentProviders,
                  }}
                  onChange={updateClaimValue}
                  label={forms.appointmentProvider}
                  labelWidth={78}
                  error={errors.appointmentProvider || backendErrors.appointmentProvider}
                  className={cx(classes.margin0, classes.fullWidth)}
                />
              </Grid>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <Select
                  filterValue={claim.insurance}
                  disabled={!isDistributionSponsor}
                  required={
                    claim
                    && claim.billingStatus !== BILLING_CLAIMS_STATUSES.appointmentTypeIssue
                    && claim.billingStatus !== BILLING_CLAIMS_STATUSES.insuranceIssue
                    && claim.billingStatus !== BILLING_CLAIMS_STATUSES.appointmentCancelled
                  }
                  dataKey='insurance'
                  columnData={{
                    isFilterable: true,
                    options: filteredInsuranceProviders,
                  }}
                  hasSearch={true}
                  totalCount={filteredInsuranceProviders?.length || 0}
                  onFilter={(searchText) => {
                    if (!searchText) {
                      setInsuranceProviders(insuranceProviders);
                    } else {
                      const newOptions = insuranceProviders?.filter(insuranceProvider => insuranceProvider.label.toLowerCase().includes(searchText.toLowerCase()));
                      setInsuranceProviders(newOptions ? newOptions : []);
                    }
                  }}
                  hideEmptyValue
                  onChange={updateClaimValue}
                  label={forms.insurance}
                  labelWidth={78}
                  error={errors.insurance || backendErrors.insurance}
                  className={cx(classes.margin0, classes.fullWidth)}
                />
              </Grid>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <FormikTextField
                  fullWidth
                  type='text'
                  name='identifier'
                  label={forms.ID}
                  hideErrorAres={true}
                  backendError={errors.identifier || backendErrors.identifier}
                  multiline
                  required={false}
                  rows={1}
                  maxRows={1}
                  onChange={(e) =>
                    updateClaimValue('identifier', e.currentTarget.value)
                  }
                  className={classes.tableInput}
                  disabled={!isDistributionSponsor}
                />
              </Grid>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <DataInput
                  disabled={!isDistributionSponsor}
                  required={true}
                  datepickerWrapperClassName={classes.datepickerWrapperClassName}
                  name='appointmentDate'
                  backendErrors={{ ...backendErrors, ...errors }}
                  updateValue={updateClaimValue}
                  maxDate={addDays(claim.appointmentDate ? new Date(claim.appointmentDate) : new Date(), 7)}
                  minDate={addDays(claim.appointmentDate ? new Date(claim.appointmentDate) : new Date(), -3)}
                />
              </Grid>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <DataInput
                  required={true}
                  datepickerWrapperClassName={classes.datepickerWrapperClassName}
                  disabled={!isDistributionSponsor}
                  name='documentDate'
                  backendErrors={{ ...backendErrors, ...errors }}
                  updateValue={updateClaimValue}
                  maxDate={new Date()}
                />
              </Grid>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <Select
                  required={true}
                  filterValue={claim.isPatientSeen}
                  dataKey='isPatientSeen'
                  columnData={{
                    isFilterable: false,
                    options: [
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ],
                  }}
                  onChange={updateClaimValue}
                  label={`${forms.patientSeen}?`}
                  labelWidth={78}
                  error={errors.isPatientSeen || backendErrors.isPatientSeen}
                  className={cx(classes.margin0, classes.fullWidth)}
                  disabled={!isDistributionSponsor}
                />
              </Grid>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <Select
                  filterValue={claim.isDocumentUploaded}
                  dataKey='isDocumentUploaded'
                  required={true}
                  columnData={{
                    isFilterable: false,
                    options: [
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ],
                  }}
                  onChange={updateClaimValue}
                  label={forms.documentUploaded}
                  labelWidth={78}
                  error={errors.isDocumentUploaded || backendErrors.isDocumentUploaded}
                  className={cx(classes.margin0, classes.fullWidth)}
                  disabled={!isDistributionSponsor}
                />
              </Grid>
              <CptInfo
                disabled={
                  !isDistributionSponsor
                  || claim.billingStatus === BILLING_CLAIMS_STATUSES.appointmentTypeIssue
                  || claim.billingStatus === BILLING_CLAIMS_STATUSES.insuranceIssue
                  || claim.billingStatus === BILLING_CLAIMS_STATUSES.appointmentCancelled
                }
                updateCPTErrorsIfNeeded={updateCPTErrorsIfNeeded}
                updateClaimValue={updateClaimValue}
                onDismiss={onDismiss}
                backendErrors={{ ...backendErrors, ...errors.activities }}
                cptCodes={cptCodes}
                modifiers={modifiers}
                activities={!claim
                || claim.billingStatus === BILLING_CLAIMS_STATUSES.appointmentTypeIssue
                || claim.billingStatus === BILLING_CLAIMS_STATUSES.insuranceIssue
                || claim.billingStatus === BILLING_CLAIMS_STATUSES.appointmentCancelled
                  ? initActivities
                  : claim && claim.activities
                }
                appointmentDate={claim.appointmentDate}
                claimStatus={claim && claim.claimStatus}
                cptStatus={claim && claim.cptStatus}
                billingStatus={claim && claim.billingStatus}
                navWidth={navWidth}
              />
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <DataInput
                  datepickerWrapperClassName={classes.datepickerWrapperClassName}
                  disabled={!isDistributionSponsor}
                  name='checkDate'
                  backendErrors={{ ...backendErrors, ...errors }}
                  updateValue={updateClaimValue}
                  maxDate={addDays(new Date(), 10)}
                />
              </Grid>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <FormikTextField
                  disabled={!isDistributionSponsor}
                  fullWidth
                  type='text'
                  hideErrorAres={true}
                  name='checkNumber'
                  label={forms.check}
                  backendError={backendErrors.checkNumber}
                  onChange={(e) =>
                    updateClaimValue('checkNumber', e.currentTarget.value)
                  }
                  className={classes.tableInput}
                />
              </Grid>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <Select
                  disabled={!isDistributionSponsor}
                  dataKey='paymentMethod'
                  filterValue={claim && claim.paymentMethod}
                  columnData={{
                    isFilterable: true,
                    options: PAYMENT_METHOD_CLAIMS_SELECTOR_OPTIONS,
                  }}
                  onChange={updateClaimValue}
                  label={forms.paymentMethod}
                  labelWidth={78}
                  error={backendErrors.paymentMethod}
                  className={cx(classes.margin0, classes.fullWidth)}
                />
              </Grid>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <FormikTextField
                  disabled={!isDistributionSponsor}
                  fullWidth
                  type='text'
                  name='notes'
                  hideErrorAres={true}
                  label={forms.comments}
                  backendError={backendErrors.notes}
                  multiline
                  rows={3}
                  maxRows={10}
                  onChange={(e) =>
                    updateClaimValue('notes', e.currentTarget.value)
                  }
                  className={classes.tableInput}
                />
              </Grid>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <DataInput
                  disabled={true}
                  name='claimGeneratedTimestamp'
                  updateValue={(name, value) =>
                    updateClaimValue(name, value)
                  }
                  backendErrors={{ ...backendErrors, ...errors }}
                  datepickerWrapperClassName={classes.datepickerWrapperClassName}
                />
              </Grid>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <FormikTextField
                  disabled={true}
                  fullWidth
                  type='text'
                  hideErrorAres={true}
                  name='createdBy'
                  label={forms.createdBy}
                  backendError={backendErrors.createdBy}
                  className={classes.tableInput}
                />
              </Grid>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <DataInput
                  disabled={true}
                  name='lastModifiedTimestamp'
                  updateValue={(name, value) =>
                    updateClaimValue(name, value)
                  }
                  backendErrors={{ ...backendErrors, ...errors }}
                  datepickerWrapperClassName={classes.datepickerWrapperClassName}
                />
              </Grid>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <FormikTextField
                  disabled={true}
                  fullWidth
                  type='text'
                  hideErrorAres={true}
                  name='modifiedBy'
                  label={forms.modifiedBy}
                  backendError={backendErrors.modifiedBy}
                  className={classes.tableInput}
                />
              </Grid>

              <Hidden only='lg'>
                <Grid item xl={9} md={6} sm={6} xs={0} />
              </Hidden>
              <Grid item xl={3} lg={4} md={6} sm={6} xs={12}>
                <Select
                  disabled={!isDistributionSponsor}
                  dataKey='claimStatus'
                  filterValue={claim && claim.claimStatus}
                  columnData={{
                    isFilterable: true,
                    options: CLAIM_STATUS_SELECTOR_OPTIONS,
                  }}
                  disabledItems={claimStatusDisabledItems}
                  onChange={updateClaimValue}
                  label={forms.claimStatus}
                  labelWidth={78}
                  error={errors.claimStatus || backendErrors.claimStatus}
                  className={cx(classes.margin0, classes.fullWidth)}
                />
              </Grid>
            </>
          </Grid>
        </Form>
      </MuiThemeProvider>
    </BlurScreen>
  );
};

ClaimForm.propTypes = {
  claim: PropTypes.shape().isRequired,
  setClaim: PropTypes.func.isRequired,
  backendErrors: PropTypes.any.isRequired,
  isDistributionSponsor: PropTypes.bool.isRequired,
  setBackendErrors: PropTypes.func.isRequired,
  cptCodes: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
  insuranceProviders: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
};

export default ClaimForm;
